import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDownWideNarrow, ArrowUpNarrowWide, FileSpreadsheet, Scale, ShoppingCart, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, fmtTL, API } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

export default function Compare() {
    const navigate = useNavigate();
    const { compareIds, toggleCompare, clearCompare, refreshCart } = useShop();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("index");
    const [sortDir, setSortDir] = useState("asc");
    const [uploading, setUploading] = useState(false);
    const [unmatched, setUnmatched] = useState([]);

    useEffect(() => {
        if (!compareIds.length) { setData([]); return; }
        api.get(`/compare?product_ids=${compareIds.join(",")}`).then((r) => setData(r.data)).catch(() => {});
    }, [compareIds]);

    const uploadExcel = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/compare/excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const ids = r.data.matched.filter((m) => m.product_id).map((m) => m.product_id);
            ids.forEach((id) => { if (!compareIds.includes(id)) toggleCompare(id); });
            setUnmatched(r.data.unmatched.map((u) => u.query));
            toast.success(`${ids.length} ürün eşleşti${r.data.unmatched.length ? `, ${r.data.unmatched.length} bulunamadı` : ""}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Excel okunamadı");
        } finally {
            setUploading(false);
        }
    };

    const suppliers = useMemo(() => {
        const map = {};
        (data || []).forEach((p) => p.offers.forEach((o) => { map[o.supplier_id] = o.supplier_name; }));
        return Object.entries(map).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "tr"));
    }, [data]);

    const sortedProducts = useMemo(() => {
        const arr = [...(data || [])];
        arr.sort((a, b) => {
            const pa = a.offers[0]?.price ?? Infinity;
            const pb = b.offers[0]?.price ?? Infinity;
            return sortDir === "asc" ? pa - pb : pb - pa;
        });
        return arr;
    }, [data, sortDir]);

    const supplierTotals = useMemo(() => {
        const totals = {};
        (data || []).forEach((p) => p.offers.forEach((o) => {
            if (!totals[o.supplier_id]) totals[o.supplier_id] = { name: o.supplier_name, city: o.supplier_city, total: 0, covered: 0, items: [] };
            totals[o.supplier_id].total += o.price;
            totals[o.supplier_id].covered += 1;
            totals[o.supplier_id].items.push({ product: p.name, unit: p.unit, price: o.price, offer_id: o.offer_id, quality: o.quality });
        }));
        return Object.entries(totals).map(([id, v]) => ({ id, ...v })).sort((a, b) => a.total - b.total);
    }, [data]);

    const addToCart = async (offerId) => {
        try {
            await api.post("/cart/items", { offer_id: offerId, qty: 1 });
            await refreshCart();
            toast.success("Sepete eklendi — Tedarikçi Bazlı Teklifler'e taşındı");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eklenemedi");
        }
    };

    const addAllFromSupplier = async (s) => {
        let n = 0;
        for (const it of s.items) {
            try { await api.post("/cart/items", { offer_id: it.offer_id, qty: 1 }); n++; } catch { /* skip */ }
        }
        await refreshCart();
        toast.success(`${n} ürün sepete eklendi`);
        navigate("/panel/sepet");
    };

    const downloadExcel = () => {
        const a = document.createElement("a");
        a.href = `${API}/compare/export?product_ids=${compareIds.join(",")}`;
        a.click();
    };

    const TABS = [["index", "İndex"], ["product", "Ürün Bazlı"], ["supplier", "Tedarik Bazlı"]];

    return (
        <div className="space-y-6" data-testid="compare-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Karşılaştırma</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Excel ile ürün listenizi yükleyin veya katalogdan ekleyin — tüm fiyatlar KDV dahildir.</p>
                </div>
                <div className="flex gap-2">
                    <a href={`${API}/compare/template`} className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-muted" data-testid="compare-template-download">
                        <FileSpreadsheet className="h-4 w-4" /> Şablon İndir
                    </a>
                    <label className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-muted ${uploading ? "opacity-60" : ""}`} data-testid="compare-excel-upload">
                        <input type="file" accept=".xlsx" className="hidden" disabled={uploading} onChange={(e) => uploadExcel(e.target.files[0])} data-testid="compare-excel-input" />
                        <Upload className="h-4 w-4" /> {uploading ? "Aranıyor..." : "Excel Yükle"}
                    </label>
                    {compareIds.length > 0 && (
                        <>
                            <Button variant="outline" onClick={downloadExcel} data-testid="compare-excel-download"><FileSpreadsheet className="mr-2 h-4 w-4" /> Excel İndir</Button>
                            <Button variant="outline" onClick={() => { clearCompare(); setUnmatched([]); }} data-testid="compare-clear"><Trash2 className="mr-2 h-4 w-4" /> Temizle</Button>
                        </>
                    )}
                </div>
            </div>

            {!compareIds.length && (
                <div className="rounded-2xl border border-dashed p-16 text-center" data-testid="compare-empty">
                    <Scale className="mx-auto h-10 w-10 text-muted-foreground" />
                    <p className="mt-4 text-sm text-muted-foreground">Katalogdan ürün ekleyin veya Excel listenizi yükleyin (ilk sütun: ürün adları).</p>
                </div>
            )}

            {unmatched.length > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="compare-unmatched">
                    <p className="text-sm font-semibold text-amber-900">Sistemde bulunamayan ürünler ({unmatched.length})</p>
                    <p className="mt-0.5 text-xs text-amber-700">Bu ürünler katalogda eşleşmedi — yazımı kontrol edin veya tedarikçilerden RFQ ile talep edin.</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {unmatched.map((u) => (
                            <span key={u} className="rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-900" data-testid={`unmatched-${u}`}>{u}</span>
                        ))}
                    </div>
                </div>
            )}

            {compareIds.length > 0 && data && (
                <>
                    <div className="flex gap-2" data-testid="compare-tabs">
                        {TABS.map(([k, l]) => (
                            <button key={k} onClick={() => setTab(k)} className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${tab === k ? "border-primary bg-primary text-primary-foreground" : "hover:border-terra-500/50"}`} data-testid={`compare-tab-${k}`}>{l}</button>
                        ))}
                    </div>

                    {tab === "index" && (
                        <div className="overflow-x-auto rounded-2xl border bg-card shadow-sm" data-testid="compare-index">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                        <th className="p-3">Ürün</th>
                                        {suppliers.map((s) => <th key={s.id} className="p-3">{s.name}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((p) => {
                                        const prices = p.offers.map((o) => o.price);
                                        const min = Math.min(...prices), max = Math.max(...prices);
                                        return (
                                            <tr key={p.product_id} className="border-b last:border-0" data-testid={`index-row-${p.product_id}`}>
                                                <td className="p-3 font-semibold">{p.name} <span className="text-xs font-normal text-muted-foreground">/{p.unit}</span></td>
                                                {suppliers.map((s) => {
                                                    const o = p.offers.find((x) => x.supplier_id === s.id);
                                                    if (!o) return <td key={s.id} className="p-3 text-muted-foreground">—</td>;
                                                    const cls = prices.length > 1 && o.price === min ? "font-bold text-emerald-700" : prices.length > 1 && o.price === max ? "font-bold text-red-600" : "";
                                                    return <td key={s.id} className={`p-3 ${cls}`} data-testid={`index-cell-${p.product_id}-${s.id}`}>{fmtTL(o.price)}</td>;
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {tab === "product" && (
                        <div className="space-y-3" data-testid="compare-product-tab">
                            <div className="flex justify-end">
                                <Button size="sm" variant="outline" onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")} data-testid="product-sort-toggle">
                                    {sortDir === "asc" ? <><ArrowUpNarrowWide className="mr-1.5 h-3.5 w-3.5" /> Düşükten Yükseğe</> : <><ArrowDownWideNarrow className="mr-1.5 h-3.5 w-3.5" /> Yüksekten Düşüğe</>}
                                </Button>
                            </div>
                            {sortedProducts.map((p) => (
                                <div key={p.product_id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-card p-4 shadow-sm" data-testid={`product-row-${p.product_id}`}>
                                    <div>
                                        <p className="font-semibold">{p.name}</p>
                                        <p className="text-xs text-muted-foreground">En iyi: {p.offers[0] ? `${fmtTL(p.offers[0].price)} (KDV dahil) — ${p.offers[0].supplier_name}` : "teklif yok"}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        {p.offers[0] && (
                                            <Button size="sm" className="bg-terra-600 text-white hover:bg-terra-700" onClick={() => addToCart(p.offers[0].offer_id)} data-testid={`product-order-${p.product_id}`}>
                                                <ShoppingCart className="mr-1.5 h-3.5 w-3.5" /> Sipariş Oluştur
                                            </Button>
                                        )}
                                        <Button size="sm" variant="ghost" onClick={() => toggleCompare(p.product_id)} data-testid={`product-remove-${p.product_id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === "supplier" && (
                        <div className="space-y-4" data-testid="compare-supplier-tab">
                            {supplierTotals.map((s, i) => (
                                <div key={s.id} className="rounded-2xl border bg-card p-5 shadow-sm" data-testid={`supplier-total-${s.id}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div>
                                            <p className="font-display font-bold">{s.name} <span className="text-xs font-normal text-muted-foreground">({s.city})</span></p>
                                            <p className="text-xs text-muted-foreground">{s.covered}/{data.length} ürünü karşılıyor</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-display text-xl font-extrabold text-terra-600" data-testid={`supplier-subtotal-${s.id}`}>{fmtTL(s.total)}</p>
                                            {i === 0 && <Badge className="border-0 bg-emerald-600 text-[10px] text-white">En Düşük Toplam</Badge>}
                                            {i === supplierTotals.length - 1 && supplierTotals.length > 1 && <Badge className="border-0 bg-red-600 text-[10px] text-white">En Yüksek Toplam</Badge>}
                                        </div>
                                    </div>
                                    <div className="mt-3 space-y-1 border-t pt-3">
                                        {s.items.map((it) => (
                                            <p key={it.offer_id} className="flex justify-between text-xs text-muted-foreground">
                                                <span>{it.product}{it.quality ? ` — ${it.quality}` : ""}</span><span className="font-medium text-foreground">{fmtTL(it.price)} / {it.unit}</span>
                                            </p>
                                        ))}
                                    </div>
                                    <div className="mt-3 flex justify-end border-t pt-3">
                                        <Button size="sm" className="bg-terra-600 text-white hover:bg-terra-700" onClick={() => addAllFromSupplier(s)} data-testid={`supplier-order-${s.id}`}>
                                            <ShoppingCart className="mr-1.5 h-3.5 w-3.5" /> Sipariş Oluştur
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
